import { Injectable } from '@angular/core';
import { CssUtils } from '@common/flare-data/util/css-utils';
import { AssetsFileService } from '@portal-core/general/services/assets-file.service';
import { forkJoin, map, Observable, shareReplay } from 'rxjs';
import { StyleSource } from '@portal-core/project-files/models/style-source.model';

// Factory stylesheets from Flare Desktop (copied during the build process from 'Source.Net\Libraries\B3.CssKit\Resources\Schemas\')
const xhtmlFilePath = 'stylesheets/editor/flare/xhtml.css';
const madcapEditorFilePath = 'stylesheets/editor/flare/MadCapEditor.css';
const madcapEditorFlareFilePath = 'stylesheets/editor/flare/MadCapEditorFlare.css';

/**
 * Loads the static Flare Desktop factory CSS files used by the LWE editor and exposes them as a
 * shared observable. Provided at the application root so all editor instances share a single
 * fetch + parse for the lifetime of the app.
 */
@Injectable({ providedIn: 'root' })
export class FactoryStyleSourcesService {
  /** Factory style sources, fetched + parsed once and replayed to every subscriber. */
  readonly factoryStyleSources$: Observable<StyleSource[]>;

  constructor(private assetsFileService: AssetsFileService) {
    this.factoryStyleSources$ = this.buildFactoryStyleSources$();
  }

  private buildFactoryStyleSources$(): Observable<StyleSource[]> {
    const createFactoryStyleSource$ = (filePath: string): Observable<StyleSource> =>
      this.assetsFileService.getTextFile$(filePath).pipe(
        map(content => {
          const assetsPath = '%assets%/' + filePath; // add path prefix to identify factory stylesheet
          const stylesheetType = content ? CssUtils.stylesheetType(content) : null;
          const css = content ? CssUtils.parse(content) : null;
          return { path: assetsPath, mediaQueries: null, css, stylesheetType };
        })
      );

    return forkJoin([
      createFactoryStyleSource$(xhtmlFilePath),
      createFactoryStyleSource$(madcapEditorFilePath),
      createFactoryStyleSource$(madcapEditorFlareFilePath)
    ]).pipe(
      // Replay to every subscriber across all editor instances. refCount: false keeps the cache
      // alive even when all current subscribers unsubscribe (each editor's load() does this).
      shareReplay({ bufferSize: 1, refCount: false })
    );
  }
}
